import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLongRightIcon, ArrowUturnRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { type WorkQueueCase, advanceWorkflow } from '../../services/workQueue';
import { useToast } from '../../providers/ToastProvider';
import { reportError } from '../../services/errorReporting';
import type { StatusPreview } from './statusPreview';
import { StatusChip } from './StatusChip';
import { invalidateCaseDetail } from './util';
import { useEscapeKey } from '../../hooks/useEscapeKey';

interface ExitHoldConfirmModalProps {
    workCase: WorkQueueCase;
    /** Current status — what the case is exiting from. */
    from: StatusPreview;
    /** Nominal resume target. The engine may rewrite it via resumeFromStatus,
     *  so this is best-effort context only. */
    to?: StatusPreview;
    onClose: () => void;
}

/** Admin confirmation for the generic engine `exit-hold` action, resumes via
 *  resumeFromStatus. No reason field; that's what "Force status" is for. */
export function ExitHoldConfirmModal({
    workCase: c,
    from,
    to,
    onClose
}: ExitHoldConfirmModalProps) {
    const queryClient = useQueryClient();
    const { showToast } = useToast();
    const [submitting, setSubmitting] = useState(false);

    useEscapeKey(onClose, !submitting);

    const handleSubmit = async (): Promise<void> => {
        setSubmitting(true);
        try {
            await advanceWorkflow(c.id, 'exit-hold');
            await invalidateCaseDetail(queryClient, c.id);
            showToast(`Exited hold for ${c.name}`, 'success');
            onClose();
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Unknown error';
            showToast('Failed to exit hold. Please try again.', 'error');
            reportError({
                source: 'ExitHoldConfirmModal.handleSubmit',
                message,
                stack: err instanceof Error ? err.stack : undefined,
                url: globalThis.location?.href ?? '',
                userAgent: navigator.userAgent,
                errorType: 'workflow_action_failure',
                metadata: { caseId: c.id }
            });
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-35 z-50 flex items-center justify-center">
            <div className="bg-white rounded-xl w-[460px] shadow-xl overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <ArrowUturnRightIcon className="w-5 h-5 text-orange-500" />
                        <span className="font-bold text-[15px] text-gray-800">Exit hold</span>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        className="text-gray-400 hover:text-gray-700 transition-colors disabled:opacity-50"
                    >
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                </div>

                <div className="px-5 py-4 space-y-3">
                    <div className="flex items-start gap-2 px-3 py-2.5 rounded-md bg-orange-50 border border-orange-300">
                        <ArrowUturnRightIcon className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                        <div className="text-xs text-orange-900 leading-relaxed space-y-1.5">
                            <p className="m-0">
                                This exits the hold and resumes the case to its prior step.
                            </p>
                            <p className="m-0 font-semibold">Are you sure you want to proceed?</p>
                        </div>
                    </div>

                    {/* Patient + transition preview */}
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-3 space-y-2.5">
                        <div className="flex items-baseline gap-2">
                            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                                Patient
                            </span>
                            <span className="text-sm font-bold text-gray-800 truncate">
                                {c.name}
                            </span>
                        </div>
                        <div className="grid grid-cols-[1fr_auto_1fr] gap-2.5 items-center">
                            <StatusChip preview={from} />
                            <ArrowLongRightIcon className="w-5 h-5 text-gray-400" />
                            {to ? (
                                <StatusChip preview={to} />
                            ) : (
                                <div className="text-[11px] text-gray-400 italic px-2.5 py-2">
                                    Target unknown
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 text-xs font-bold bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="px-4 py-2 rounded-md bg-orange-600 text-white text-xs font-bold hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submitting ? 'Exiting…' : 'Exit hold'}
                    </button>
                </div>
            </div>
        </div>
    );
}
